import axios, { InternalAxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/react';
import { QueryClient } from '@tanstack/react-query';
import { ErrorData } from '@/api/models';
import { shouldReportServerError } from '@/api/common/server-error-reporting';
import { HTTP_STATUS } from '@/api/common/http-status';
import { SIGN_IN } from '@/lib/constants/routes';
import Cookies from 'js-cookie';
import { CSRF_TOKEN } from '@/lib/constants/tokens';
import { removeTokenCookies } from '@/lib/auth/utils';
import { resolveRuntimeBasePath, runtimeConfig } from '@/lib/runtime-config';

// Extend Axios types to support skipAuth flag
declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuth?: boolean;
  }
}

const BASE_URL = runtimeConfig.VITE_BASE_URL;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      retry: 1,
    },
  },
});

const BASE_PATH = resolveRuntimeBasePath();
const BACKOFFICE_SIGN_IN = `${BASE_PATH}${SIGN_IN}`;

const REDIRECT_SETTLE_MS = 100;

const redirectToSignIn = async (): Promise<void> => {
  removeTokenCookies();

  if (window.location.pathname.includes(SIGN_IN)) return;

  window.location.replace(BACKOFFICE_SIGN_IN);
  await new Promise((resolve) => setTimeout(resolve, REDIRECT_SETTLE_MS));
};

/**
 * Lock dei 401 contemporanei: chi arriva mentre il redirect è in corso
 * aspetta lo stesso. Il lock si libera in `.finally`, che gira sempre DOPO
 * l'assegnazione: con un `finally` dentro la funzione, un redirect saltato
 * (già sul login, nessun `await`) azzerava il lock prima che venisse
 * assegnato, e il lock restava una promise risolta per sempre.
 */
let pendingAuthFailure: Promise<void> | null = null;

const handleAuthFailure = (): Promise<void> => {
  pendingAuthFailure ??= redirectToSignIn().finally(() => {
    pendingAuthFailure = null;
  });
  return pendingAuthFailure;
};

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  skipAuth?: boolean;
}

export const client = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

client.interceptors.request.use(
  async (config: CustomAxiosRequestConfig) => {
    const method = (config.method || 'GET').toUpperCase();
    const needsCsrf = !['GET', 'HEAD', 'OPTIONS'].includes(method);
    const csrfToken = Cookies.get(CSRF_TOKEN);

    if (!config.skipAuth && needsCsrf && csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for centralized error handling
client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as
      | CustomAxiosRequestConfig
      | undefined;

    // Con `skipAuth` (login, reset password) un 401 è la risposta attesa
    // della richiesta, non una sessione scaduta: niente logout né redirect.
    if (
      error.response?.status === HTTP_STATUS.UNAUTHORIZED &&
      originalRequest &&
      !originalRequest.skipAuth
    ) {
      await handleAuthFailure();
    }

    // Capture server errors (5xx) to Sentry, except a temporarily unavailable DB
    if (shouldReportServerError(error.response)) {
      Sentry.captureException(error, {
        tags: {
          url: error.config?.url,
          method: error.config?.method?.toUpperCase(),
        },
        extra: {
          statusCode: error.response.status,
          responseData: error.response.data,
        },
      });
    }

    if (error.response) {
      throw new ErrorData(await readErrorBody(error.response.data));
    }
    throw error;
  }
);

/**
 * Il corpo dell'errore come oggetto: con `responseType: 'blob'` (download)
 * axios consegna anche l'errore come Blob, e il motivo i18n del server
 * andrebbe perso. Un corpo non JSON resta un errore senza messaggio.
 */
export async function readErrorBody(data: unknown): Promise<ErrorData | null> {
  if (typeof Blob === 'undefined' || !(data instanceof Blob))
    return data as ErrorData | null;
  try {
    return JSON.parse(await data.text()) as ErrorData;
  } catch {
    return null;
  }
}
